import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { Observable, Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { ConfigService } from '../shared/services/config.service';
import { Login } from '../auth/state/actions/auth.actions';
import { AuthService } from '../auth/auth.service';
import { CookieService } from 'ngx-cookie-service';
import { User } from 'oidc-client';
import { AppState } from '@app/state/app.state';

@Injectable({
  providedIn: 'root',
})
export class TokenInterceptor implements HttpInterceptor {
  constructor(
    private configService: ConfigService,
    private store: Store<AppState>,
    private authService: AuthService,
    private cookieService: CookieService
  ) {}

  intercept(request: HttpRequest<any>, nextHttp: HttpHandler): Observable<HttpEvent<any>> {
    return new Observable<HttpEvent<any>>((observer) => {
      let outerSub: Subscription;
      let innerSub: Subscription;

      const handling = {
        next: (x) => observer.next(x),
        error: (err) => {
          if (err.status === 401) {
            this.store.dispatch(new Login());
          }
          observer.error(err);
        },
        complete: () => observer.complete(),
      };

      if (
        this.configService.config &&
        (request.url.startsWith(this.configService.config.endpoints.apiUrl) ||
          request.url.startsWith(this.configService.config.endpoints.supplierManagerPortalApi) ||
          request.url.startsWith(this.configService.config.endpoints.utilityHubRoot))
      ) {
        outerSub = this.checkOidcAuthentication().subscribe((user) => {
          innerSub = nextHttp.handle(this.cloneRequest(user, request)).subscribe(handling);
        });
      } else {
        innerSub = nextHttp.handle(request).subscribe(handling);
      }

      // Teardown: unsubscribe if the outer Observable is cancelled before completion
      return () => {
        outerSub?.unsubscribe();
        innerSub?.unsubscribe();
      };
    });
  }

  checkOidcAuthentication(): Observable<User | null> {
    return this.authService.getUser().pipe(
      take(1),
      map((user) => (user && !user.expired ? user : null))
    );
  }

  cloneRequest(user, request) {
    if (user && !user.expired) {
      const token = user.access_token;
      return request.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
          'x-active-organisation': this.cookieService.get(`active-organisation-${user.profile.sub}`),
        },
      });
    }

    return request;
  }
}
